/**
 * ShieldWall — Main CAPTCHA Widget
 *
 * Orchestrates all ShieldWall layers:
 *   1. Behavioral biometrics (useBehaviorCollector)
 *   2. Honeypot trap (useHoneypot)
 *   3. Browser fingerprinting + consistency check
 *   4. Memory-hard Proof-of-Work
 *   5. Server challenge/verify round-trip
 *
 * WCAG 2.2 SC 3.3.8 compliant — no image or audio puzzles.
 */

import React, { useEffect, useState, useCallback } from "react";
import { useBehaviorCollector } from "../hooks/useBehaviorCollector";
import { useHoneypot } from "../hooks/useHoneypot";
import {
  collectFingerprint,
  scoreBrowserFingerprint,
  analyzeConsistency,
} from "../lib/fingerprint";
import { solveMemoryHardPoW } from "../lib/pow";
import type { PoWResult } from "../lib/pow";
import { solveArgon2PoW } from "../lib/pow-argon2";
import { generateChallenge, verifyChallenge } from "../lib/challenges";
import { encryptPayload, isEncryptionSupported } from "../lib/payload-crypto";
import { DEFAULT_CONFIG } from "../lib/config";
import type {
  ShieldWallConfig,
  ChallengeResponse,
  VerifyResponse,
} from "../lib/config";
import type { Challenge } from "../lib/challenges";
import "./shieldwall-widget.css";

/** Why a verification attempt could not produce a verdict the host can use. */
export type ShieldWallErrorKind =
  /** Challenge or verify call never completed — offline, DNS filter, ad blocker, CORS. */
  | "network"
  /** Server returned 429 on /challenge. */
  | "rate-limited"
  /** Server scored the session below the challenge threshold. */
  | "rejected"
  /** Server demanded an algorithm this browser cannot run (e.g. argon2id without WASM). */
  | "unsupported";

/** Every verify round-trip, whatever the verdict. */
export interface ShieldWallOutcome {
  verdict: "PASSED" | "CHALLENGE_REQUIRED" | "REJECTED";
  score: number;
  breakdown: Record<string, number>;
  /** SW-901 codes. Additive — ignore codes you do not recognise. */
  reasonCodes: string[];
  /** Redeem server-side via `POST /api/v1/siteverify`. Null when no siteKey was bound. */
  responseToken: string | null;
  token: string | null;
}

export interface ShieldWallError {
  kind: ShieldWallErrorKind;
  message: string;
  reasonCodes: string[];
}

export interface ShieldWallWidgetProps {
  /** Called with the signed JWT token when verification passes */
  onVerified: (token: string) => void;
  /**
   * Called after every verify round-trip, before the widget acts on the
   * verdict. Optional — hosts that only need the pass path can ignore it.
   * Instrumentation hosts use this to record CHALLENGE_REQUIRED and REJECTED,
   * which `onVerified` cannot express.
   */
  onResult?: (outcome: ShieldWallOutcome) => void;
  /** Called when the widget enters the interactive challenge phase. */
  onChallengeRequired?: (info: { escalationLevel: number; challengeType: string }) => void;
  /** Called when no verdict could be produced, or the verdict was REJECTED. */
  onError?: (error: ShieldWallError) => void;
  /** API base URL. Defaults to config.apiBase ("/api/captcha") */
  apiBase?: string;
  /** Action context: login, signup, payment, form_submit, coupon_redeem, generic */
  action?: string;
  /**
   * Public site key (`sw_site_…`). Attributes telemetry to this tenant site so
   * console Sentinel (site-scoped) can see the events. Omit only for unscoped
   * dogfood / local demos.
   */
  siteKey?: string;
  /** Optional config overrides */
  config?: Partial<ShieldWallConfig>;
  /** Compact style mode matching Altcha/Turnstile (default: true) */
  compact?: boolean;
}

// ─── State Machine ──────────────────────────────────────────

type Phase =
  | { name: "idle" }
  | { name: "solving"; progress: number }
  | { name: "verifying" }
  | { name: "challenge"; question: Challenge; seed: number; enabledTypes: string[] }
  | { name: "passed"; token: string }
  | { name: "error"; message: string };

// ─── Component ──────────────────────────────────────────────

export default function ShieldWallWidget({
  onVerified,
  onResult,
  onChallengeRequired,
  onError,
  apiBase,
  action: actionProp,
  siteKey,
  config: configProp,
  compact = true,
}: ShieldWallWidgetProps) {
  const cfg = { ...DEFAULT_CONFIG, ...configProp };
  const base = apiBase ?? cfg.apiBase;
  const action = actionProp ?? cfg.action ?? "generic";

  const { getScore } = useBehaviorCollector();
  const honeypot = useHoneypot();

  const [phase, setPhase] = useState<Phase>({ name: "idle" });
  const [answerInput, setAnswerInput] = useState("");

  /**
   * Run one full challenge → PoW solve → verify cycle.
   * Called on mount, and again after a correct cognitive challenge answer.
   */
  const runVerification = useCallback(async () => {
    try {
      // ── Step 1: Fetch challenge (with action context) ─────────────────
      const challengeRes = await fetch(`${base}/challenge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, ...(siteKey ? { siteKey } : {}) }),
      });
      if (!challengeRes.ok) {
        if (challengeRes.status === 429) {
          const message = "Too many attempts. Please wait and try again.";
          setPhase({ name: "error", message });
          onError?.({ kind: "rate-limited", message, reasonCodes: ["rate-limit-exceeded"] });
          return;
        }
        throw new Error("Failed to fetch challenge");
      }
      const challengeData: ChallengeResponse = await challengeRes.json();

      // ── Step 2: Collect browser fingerprint ─────────────────────────────
      const fp = collectFingerprint();
      const fpResult = scoreBrowserFingerprint(fp);

      // ── Step 3: Solve PoW ───────────────────────────────────────────────
      // The server picks the algorithm (bound into the HMAC v2 canonical):
      // "argon2id" (true memory-hard, WASM) or the SHA-256 chain. The server
      // re-verifies with the same algo, so we must honour what it issued.
      setPhase({ name: "solving", progress: 0 });
      const onSolveProgress = (pct: number) => setPhase({ name: "solving", progress: pct });
      let powResult: PoWResult;
      if (challengeData.algo === "argon2id") {
        const argon = await solveArgon2PoW(
          challengeData.nonce, challengeData.difficulty, onSolveProgress
        );
        if (!argon) throw new Error("Argon2id is required but WebAssembly is unavailable");
        powResult = { ...argon, memoryBlocks: 0 } as unknown as PoWResult;
      } else {
        powResult = await solveMemoryHardPoW(
          challengeData.nonce, challengeData.difficulty, onSolveProgress, cfg.pow
        );
      }

      // ── Step 4: Behavioral score + cross-signal consistency ─────────────
      // Pass nonce for cryptographic replay protection — binds behavior data
      // to this specific challenge so recorded sessions can't be replayed.
      const behaviorResult = getScore(challengeData.nonce);
      const consistency = analyzeConsistency(fp, behaviorResult.stats);

      // ── Step 5: POST /verify ─────────────────────────────────────────────
      // Field names must match C# CaptchaPayload record exactly.
      // Note: "seed" not "challengeSeed" — different from VerifyPayload type.
      // Compute fingerprint hash for device binding + clustering
      const fingerprintHash = `${fp.canvasHash}-${fp.webglRenderer ?? "none"}-${fp.hardwareConcurrency}-${fp.platform ?? "none"}`;

      setPhase({ name: "verifying" });
      const verifyBody = {
        nonce: challengeData.nonce,
        timestamp: challengeData.timestamp,
        difficulty: challengeData.difficulty,
        seed: challengeData.challengeSeed, // C# expects "seed"
        signature: challengeData.signature,
        powNonce: powResult.nonce, // integer nonce found by solver
        powSolveTimeMs: powResult.solveTimeMs,
        powType: powResult.type,
        behaviorScore: behaviorResult.score,
        browserScore: fpResult.score,
        consistencyPenalty: consistency.penalty,
        honeypotTriggered: honeypot.isTriggered(),
        browserSignals: {
          webdriver: fp.webdriver,
          canvasHash: fp.canvasHash,
          canvasStable: fp.canvasStable,
          webglRenderer: fp.webglRenderer,
          webglExtensions: fp.webglExtensions,
          audioSampleRate: fp.audioSampleRate,
          audioMaxChannels: fp.audioMaxChannels,
          hasPlugins: fp.hasPlugins,
          hasLanguages: fp.hasLanguages,
          hardwareConcurrency: fp.hardwareConcurrency,
          platform: fp.platform,
          permissionState: fp.permissionState,
          sensorSamples: behaviorResult.sensorSamples,
          nonceBinding: behaviorResult.nonceBinding,
          nonceBindingMeta: behaviorResult.nonceBindingMeta,
        },
        action: challengeData.action,
        fingerprintHash,
        // Echo v2 canonical fields so server reconstructs the exact signed payload.
        // Omitted → server falls back to v1 (only accepted during the rollout grace window).
        mode: challengeData.mode,
        algo: challengeData.algo,
        ...(siteKey ? { siteKey } : {}),
      };

      // ── Step 5b: Encrypt payload if server provided a public key ─────────
      // When encrypted, the Network tab shows only opaque ciphertext.
      // Falls back to plaintext when no publicKey (backward compat + dev mode).
      let requestBody: string;
      if (challengeData.publicKey && isEncryptionSupported()) {
        const envelope = await encryptPayload(challengeData.publicKey, verifyBody);
        // Include nonce outside the envelope so the server can look up the
        // RSA private key without decrypting first.
        requestBody = JSON.stringify({ encrypted: true, nonce: challengeData.nonce, envelope });
      } else {
        requestBody = JSON.stringify(verifyBody);
      }

      const verifyRes = await fetch(`${base}/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: requestBody,
      });
      const verifyData: VerifyResponse = await verifyRes.json();

      // Report the raw outcome before acting on it, so instrumentation hosts
      // see CHALLENGE_REQUIRED and REJECTED — verdicts onVerified cannot carry.
      onResult?.({
        verdict: verifyData.verdict,
        score: verifyData.score,
        breakdown: verifyData.breakdown ?? {},
        reasonCodes: verifyData.reasonCodes ?? [],
        responseToken: verifyData.responseToken ?? null,
        token: verifyData.token,
      });

      // ── Step 6: Handle verdict ───────────────────────────────────────────
      if (verifyData.verdict === "PASSED" && verifyData.token) {
        setPhase({ name: "passed", token: verifyData.token });
        onVerified(verifyData.token);
        return;
      }

      if (verifyData.verdict === "CHALLENGE_REQUIRED") {
        onChallengeRequired?.({
          escalationLevel: challengeData.escalationLevel,
          challengeType: challengeData.serverChallenge?.challengeType ?? "client",
        });
        // Nonce is now consumed — the next runVerification() will fetch a new one.
        // Use server-side challenge if provided (escalation >= 2), else generate client-side.
        if (challengeData.serverChallenge) {
          setPhase({
            name: "challenge",
            question: {
              question: challengeData.serverChallenge.question,
              answer: "__server__", // Answer validated server-side
              inputType: challengeData.serverChallenge.inputType,
              label: challengeData.serverChallenge.label,
              challengeType: challengeData.serverChallenge.challengeType,
            },
            seed: challengeData.challengeSeed,
            enabledTypes: challengeData.enabledChallenges,
          });
        } else {
          const question = generateChallenge(
            challengeData.challengeSeed,
            challengeData.enabledChallenges
          );
          setPhase({
            name: "challenge",
            question,
            seed: challengeData.challengeSeed,
            enabledTypes: challengeData.enabledChallenges,
          });
        }
        return;
      }

      // REJECTED
      const rejectMessage = "Verification failed. Please refresh and try again.";
      setPhase({ name: "error", message: rejectMessage });
      onError?.({
        kind: "rejected",
        message: rejectMessage,
        reasonCodes: verifyData.reasonCodes ?? [],
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Verification error";
      setPhase({ name: "error", message });
      onError?.({
        // A missing WASM runtime is a capability gap, not a transport failure —
        // hosts offer different fallbacks for the two.
        kind: /WebAssembly/i.test(message) ? "unsupported" : "network",
        message,
        reasonCodes: [],
      });
    }
  }, [
    base,
    action,
    siteKey,
    cfg.pow,
    getScore,
    honeypot,
    onVerified,
    onResult,
    onChallengeRequired,
    onError,
  ]);

  /** Handle cognitive challenge form submission */
  const handleChallengeSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (phase.name !== "challenge") return;

      if (!verifyChallenge(answerInput, phase.question.answer)) {
        // Wrong answer — rotate to a new question from the same pool
        const nextQuestion = generateChallenge(
          Math.floor(Math.random() * 1000),
          phase.enabledTypes
        );
        setPhase({ ...phase, question: nextQuestion });
        setAnswerInput("");
        return;
      }

      // Correct answer → fetch a fresh challenge and run full verification.
      // The original nonce was consumed during the first verify call.
      setAnswerInput("");
      await runVerification();
    },
    [phase, answerInput, runVerification]
  );

  // Start verification automatically on mount
  useEffect(() => {
    runVerification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Honeypot field (off-screen, WCAG: aria-hidden + tabIndex=-1) ──────────
  const honeypotField = (
    <div
      style={{
        position: "absolute",
        left: "-9999px",
        width: "1px",
        height: "1px",
        overflow: "hidden",
      }}
      aria-hidden="true"
    >
      <label htmlFor="sw-website-url">Website URL</label>
      <input
        id="sw-website-url"
        name="website_url"
        type="text"
        tabIndex={-1}
        autoComplete="off"
        onChange={honeypot.onChange}
        onFocus={honeypot.onFocus}
      />
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────

  if (compact) {
    const isSolving = phase.name === "solving";
    const isVerifying = phase.name === "verifying";
    const isPassed = phase.name === "passed";
    const isError = phase.name === "error";
    const isChallenge = phase.name === "challenge";

    return (
      <div className={`sw-widget sw-widget--compact sw-widget--${phase.name}`}>
        {honeypotField}
        <div className="sw-compact-bar">
          <div className="sw-compact-left">
            {isSolving || isVerifying ? (
              <div className="sw-spinner" />
            ) : isPassed ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" className="sw-checkmark-icon">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : isError ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            ) : (
              <div className="sw-checkbox" />
            )}
            <span className="sw-compact-status">
              {isSolving && `Verifying... ${Math.round(phase.progress)}%`}
              {isVerifying && "Completing check..."}
              {isPassed && "Verified Human"}
              {isError && "Verification failed"}
              {phase.name === "idle" && "Secure check active"}
              {isChallenge && "Verification required"}
            </span>
          </div>
        </div>

        {isChallenge && (
          <div className="sw-compact-challenge">
            <form onSubmit={handleChallengeSubmit} noValidate style={{ marginTop: 12 }}>
              <label htmlFor="sw-challenge-input" className="sw-challenge__label" style={{ display: "block", marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                {phase.question.label}: {phase.question.question}
              </label>
              <div className="sw-challenge-input-row" style={{ display: "flex", gap: 8 }}>
                <input
                  id="sw-challenge-input"
                  type={phase.question.inputType}
                  value={answerInput}
                  onChange={(e) => setAnswerInput(e.target.value)}
                  autoComplete="off"
                  required
                  aria-required="true"
                  className="sw-challenge__input"
                  style={{ flex: 1, padding: "8px 12px", fontSize: 13 }}
                />
                <button type="submit" className="sw-challenge__submit" style={{ padding: "8px 16px", fontSize: 13 }}>
                  Submit
                </button>
              </div>
            </form>
          </div>
        )}

        {isError && (
          <div className="sw-compact-error-actions">
            <button onClick={runVerification} className="sw-widget__retry" type="button">
              Try again
            </button>
          </div>
        )}

        <div className="sw-compact-brand">
          <span className="sw-brand-line">ShieldWall · Privacy protected</span>
        </div>
      </div>
    );
  }

  if (phase.name === "idle" || phase.name === "solving") {
    const progress = phase.name === "solving" ? phase.progress : 0;
    return (
      <div className="sw-widget sw-widget--loading">
        {honeypotField}
        <div
          role="progressbar"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Security verification in progress"
          className="sw-progress"
        >
          <div
            className="sw-progress__bar"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="sw-widget__status">Verifying you&apos;re human…</p>
      </div>
    );
  }

  if (phase.name === "verifying") {
    return (
      <div className="sw-widget sw-widget--verifying">
        {honeypotField}
        <p className="sw-widget__status" aria-live="polite">
          Completing verification…
        </p>
      </div>
    );
  }

  if (phase.name === "challenge") {
    const { question } = phase;
    return (
      <div className="sw-widget sw-widget--challenge">
        {honeypotField}
        <form onSubmit={handleChallengeSubmit} noValidate>
          <label htmlFor="sw-challenge-input" className="sw-challenge__label">
            {question.label}: {question.question}
          </label>
          <input
            id="sw-challenge-input"
            type={question.inputType}
            value={answerInput}
            onChange={(e) => setAnswerInput(e.target.value)}
            autoComplete="off"
            required
            aria-required="true"
            className="sw-challenge__input"
          />
          <button type="submit" className="sw-challenge__submit">
            Submit
          </button>
        </form>
      </div>
    );
  }

  if (phase.name === "passed") {
    return (
      <div
        className="sw-widget sw-widget--passed"
        role="status"
        aria-live="polite"
      >
        {honeypotField}
        <span className="sw-widget__status">Verification complete ✓</span>
      </div>
    );
  }

  // error state
  return (
    <div
      className="sw-widget sw-widget--error"
      role="alert"
      aria-live="assertive"
    >
      {honeypotField}
      <p className="sw-widget__error">{phase.message}</p>
      <button
        onClick={runVerification}
        className="sw-widget__retry"
        type="button"
      >
        Try again
      </button>
    </div>
  );
}
